import { useState, type ReactNode } from "react";
import { Trans } from "@lingui/react/macro";

import { Button } from "src/@/components/ui/button";
import { Checkbox } from "src/@/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "src/@/components/ui/dialog";
import { OrderField, getOrderColumns } from "#orders/lib";

type ExportColumnsDialogProps = {
    defaultColumns: readonly OrderField[];
    availableColumns: readonly OrderField[];
    isSnackTeam?: boolean;
    onExport: (columns: readonly OrderField[]) => void;
    trigger: ReactNode;
};

export const ExportColumnsDialog = ({
    defaultColumns,
    availableColumns,
    isSnackTeam = false,
    onExport,
    trigger,
}: ExportColumnsDialogProps) => {
    const [open, setOpen] = useState(false);
    const [exportType, setExportType] = useState<"recommended" | "custom">(
        "recommended",
    );
    const [selectedColumns, setSelectedColumns] = useState<OrderField[]>([
        ...defaultColumns,
    ]);
    const columns = getOrderColumns();
    const selectableColumns = availableColumns.filter(
        (key) => !columns[key].snackTeamOnly || isSnackTeam,
    );
    const customColumns = selectedColumns.filter((key) =>
        selectableColumns.includes(key),
    );

    const toggleColumn = (column: OrderField) => {
        setSelectedColumns((current) =>
            current.includes(column)
                ? current.filter((key) => key !== column)
                : [...current, column],
        );
    };

    const handleExport = () => {
        const selected =
            exportType === "recommended" ? defaultColumns : customColumns;
        onExport(selected);
        setOpen(false);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>
                        <Trans>Export CSV</Trans>
                    </DialogTitle>
                    <DialogDescription>
                        <Trans>
                            Choose the recommended columns or select a custom
                            set.
                        </Trans>
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3">
                    <button
                        type="button"
                        onClick={() => setExportType("recommended")}
                        className={`rounded-md border p-3 text-left ${
                            exportType === "recommended"
                                ? "border-primary bg-primary/5"
                                : "border-border"
                        }`}
                    >
                        <p className="font-medium">
                            <Trans>Recommended</Trans>
                        </p>
                        <p className="text-sm text-muted-foreground">
                            <Trans>
                                Use the default columns for this export.
                            </Trans>
                        </p>
                    </button>
                    <button
                        type="button"
                        onClick={() => setExportType("custom")}
                        className={`rounded-md border p-3 text-left ${
                            exportType === "custom"
                                ? "border-primary bg-primary/5"
                                : "border-border"
                        }`}
                    >
                        <p className="font-medium">
                            <Trans>Custom</Trans>
                        </p>
                        <p className="text-sm text-muted-foreground">
                            <Trans>
                                Choose exactly which columns to include.
                            </Trans>
                        </p>
                    </button>
                    {exportType === "custom" && (
                        <div className="max-h-64 overflow-y-auto rounded-md border p-3">
                            <div className="mb-3 flex gap-2 border-b pb-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setSelectedColumns([...selectableColumns])
                                    }
                                >
                                    <Trans>Toggle all</Trans>
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSelectedColumns([])}
                                >
                                    <Trans>Untoggle all</Trans>
                                </Button>
                            </div>
                            <div className="grid gap-3 sm:grid-cols-2">
                                {selectableColumns.map((key) => (
                                    <label
                                        key={key}
                                        className="flex items-center gap-2 text-sm"
                                    >
                                        <Checkbox
                                            checked={selectedColumns.includes(
                                                key,
                                            )}
                                            onCheckedChange={() =>
                                                toggleColumn(key)
                                            }
                                        />
                                        {columns[key].label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
                        onClick={handleExport}
                        disabled={
                            exportType === "custom" &&
                            customColumns.length === 0
                        }
                    >
                        <Trans>Export</Trans>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
